import { useState } from 'react';
import { useSelector } from 'react-redux';
import { SerializedError } from '@reduxjs/toolkit';

import { IFieldValue } from 'Scripts/Slices/loginPage';
import { IRootState } from 'Scripts/store';
import { ICheckboxModel } from 'Scripts/Types/Litium';

import { useLazyGetModalContentQuery } from 'Services/formPageApi';

import Markup from 'Components/Markup';
import Modal from 'Components/Modal';

import CheckBoxIcon from 'Icons/checkbox.svg?url';

interface CheckboxFieldProps {
    field: ICheckboxModel;
    handleInputChange: (data: IFieldValue) => void;
    renderAsHtml?: boolean;
    loading?: boolean;
    executeFunctionByName: (
        functionName: string,
        data: Record<string, any>,
        event: EventTarget & HTMLInputElement
    ) => void;
}

const CheckboxField = ({
    field,
    handleInputChange,
    loading,
    executeFunctionByName,
    renderAsHtml = false,
}: CheckboxFieldProps) => {
    const closeButtonLabel = useSelector(
        (state: IRootState) => state.formPage?.closeAgreementsButtonLabel
    );
    const {
        disabled,
        required,
        checked,
        name,
        placeHolder,
        value,
        handlers,
        label,
    } = field;

    const [
        getModalContent,
        {
            isLoading: modalContentIsLoading,
            data: modalContentData,
            isError: modalContentIsError,
            error: modalContentError,
        },
    ] = useLazyGetModalContentQuery();

    const [isOpenTermsAndConditionModal, setIsOpenTermsAndConditionModal] =
        useState(false);

    const handleOnClick = (e: React.MouseEvent<HTMLElement>) => {
        if (e.target instanceof HTMLAnchorElement) {
            e.preventDefault();
            const { href } = e.target;
            const pageId = href.split(':').pop();
            getModalContent(pageId);
            setIsOpenTermsAndConditionModal(true);
        }
    };

    const getErrorMsg = () => {
        if ('status' in modalContentError) {
            const { message } = modalContentError.data as SerializedError;
            return message;
        }
        return modalContentError.message;
    };

    return (
        <div className="font-sm mb-5 flex font-roboto text-black transition-opacity duration-layout">
            <input
                type="checkbox"
                className="peer sr-only"
                readOnly={loading || disabled}
                required={required}
                checked={checked}
                name={name}
                id={name}
                placeholder={placeHolder}
                value={value}
                onChange={event => {
                    if (handlers?.onChange) {
                        const { functionName, data } = handlers.onChange;
                        executeFunctionByName(functionName, data, event.target);
                    }
                    return handleInputChange({
                        value: event.target.checked ? 'true' : 'false',
                        type: event.target.type,
                        name,
                    });
                }}
            />
            <label
                style={
                    {
                        '--check-box-icon': `url("${CheckBoxIcon}")`,
                    } as React.CSSProperties
                }
                htmlFor={name}
                className="mb-4 flex w-full origin-center cursor-pointer select-none items-start transition-colors duration-layout before:mr-4 before:inline-block before:h-6 before:w-6 before:flex-shrink-0 before:flex-grow-0 before:basis-auto before:border before:border-black before:peer-checked:bg-no-repeat before:peer-checked:[background-image:var(--check-box-icon)] before:peer-focus:outline"
            >
                {renderAsHtml ? (
                    <>
                        <Markup
                            onClickHandler={handleOnClick}
                            html={label}
                            cssClass="prose-checkbox"
                        />
                        <Modal
                            isLoading={modalContentIsLoading}
                            title={
                                !modalContentIsError
                                    ? modalContentData?.dialogTitle
                                    : getErrorMsg()
                            }
                            className="h-fit max-h-[80vh] overflow-auto"
                            onClose={() =>
                                setIsOpenTermsAndConditionModal(false)
                            }
                            isOpened={isOpenTermsAndConditionModal}
                            ariaCloseButtonTranslation={closeButtonLabel}
                            backButtonTranslation={closeButtonLabel}
                        >
                            <Markup html={modalContentData?.dialogContent} />
                        </Modal>
                    </>
                ) : (
                    <div className="prose prose-checkbox">
                        <p>
                            <span className="text-sm">{label}</span>
                        </p>
                    </div>
                )}
                {required && <span className="whitespace-pre"> *</span>}
            </label>
        </div>
    );
};

export default CheckboxField;
